import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { 
  X, Users, CreditCard, BarChart3, Shield, Settings,
  Search, Filter, MoreVertical, Mail, Trash2, Edit,
  TrendingUp, DollarSign, UserCheck, AlertTriangle,
  RefreshCw, Download, ChevronDown
} from 'lucide-react';
import pb from '../../lib/pocketbase';

interface AdminConsoleProps {
  onClose: () => void;
}

type TabType = 'overview' | 'users' | 'subscriptions' | 'settings';

interface PlatformStats {
  totalUsers: number;
  activeUsers: number;
  freeUsers: number;
  proUsers: number;
  enterpriseUsers: number;
  monthlyRevenue: number;
  reportsProcessed: number;
}

interface UserRecord {
  id: string;
  email: string;
  name: string;
  created: string;
  plan: string;
  status: string;
  lastActive?: string;
}

export function AdminConsole({ onClose }: AdminConsoleProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
    { id: 'users' as TabType, label: 'Users', icon: Users },
    { id: 'subscriptions' as TabType, label: 'Subscriptions', icon: CreditCard },
    { id: 'settings' as TabType, label: 'Settings', icon: Settings },
  ];

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Console</h1>
              <p className="text-white/70 text-sm">Platform Management</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-700 bg-slate-800/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all relative
                ${activeTab === tab.id 
                  ? 'text-white' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'subscriptions' && <SubscriptionsTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// OVERVIEW TAB
// ============================================
function OverviewTab() {
  const [stats, setStats] = useState<PlatformStats>({
    totalUsers: 0,
    activeUsers: 0,
    freeUsers: 0,
    proUsers: 0,
    enterpriseUsers: 0,
    monthlyRevenue: 0,
    reportsProcessed: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Try to fetch real data from PocketBase
      const users = await pb.collection('users').getList(1, 1, { 
        skipTotal: false 
      });
      
      // For now, use mock data with real user count if available
      setStats({
        totalUsers: users.totalItems || 24,
        activeUsers: Math.floor((users.totalItems || 24) * 0.7),
        freeUsers: Math.floor((users.totalItems || 24) * 0.65),
        proUsers: Math.floor((users.totalItems || 24) * 0.25),
        enterpriseUsers: Math.floor((users.totalItems || 24) * 0.1),
        monthlyRevenue: 1247,
        reportsProcessed: 156,
      });
    } catch (error) {
      // Use mock data if PocketBase fails
      setStats({
        totalUsers: 24,
        activeUsers: 17,
        freeUsers: 16,
        proUsers: 5,
        enterpriseUsers: 3,
        monthlyRevenue: 1247,
        reportsProcessed: 156,
      });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          label="Total Users" 
          value={stats.totalUsers} 
          icon={Users}
          color="blue"
          loading={loading}
        />
        <StatCard 
          label="Active This Month" 
          value={stats.activeUsers} 
          icon={UserCheck}
          color="green"
          loading={loading}
        />
        <StatCard 
          label="Monthly Revenue" 
          value={`$${stats.monthlyRevenue}`} 
          icon={DollarSign}
          color="emerald"
          loading={loading}
        />
        <StatCard 
          label="Reports Processed" 
          value={stats.reportsProcessed} 
          icon={TrendingUp}
          color="purple"
          loading={loading}
        />
      </div>

      {/* Subscription Breakdown */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
        <h3 className="text-white font-medium mb-4">Subscription Breakdown</h3>
        <div className="space-y-3">
          <ProgressBar label="Free" value={stats.freeUsers} total={stats.totalUsers} color="slate" />
          <ProgressBar label="Pro" value={stats.proUsers} total={stats.totalUsers} color="blue" />
          <ProgressBar label="Enterprise" value={stats.enterpriseUsers} total={stats.totalUsers} color="purple" />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickAction icon={Users} label="View All Users" onClick={() => {}} />
        <QuickAction icon={Mail} label="Send Announcement" onClick={() => {}} />
        <QuickAction icon={Download} label="Export Data" onClick={() => {}} />
        <QuickAction icon={RefreshCw} label="Refresh Stats" onClick={loadStats} />
      </div>
    </div>
  );
}

// ============================================
// USERS TAB
// ============================================
function UsersTab() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState<string>('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await pb.collection('users').getList(1, 50, {
        sort: '-created',
      });
      
      setUsers(result.items.map(item => ({
        id: item.id,
        email: item.email,
        name: item.name || 'Unknown',
        created: item.created,
        plan: 'free', // Would come from subscription data
        status: 'active',
      })));
    } catch (error) {
      // Mock data if PocketBase fails
      setUsers([
        { id: '1', email: 'john@example.com', name: 'John Doe', created: '2024-01-15', plan: 'pro', status: 'active' },
        { id: '2', email: 'jane@company.com', name: 'Jane Smith', created: '2024-02-20', plan: 'enterprise', status: 'active' },
        { id: '3', email: 'bob@agency.org', name: 'Bob Wilson', created: '2024-03-10', plan: 'free', status: 'active' },
      ]);
    }
    setLoading(false);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = filterPlan === 'all' || user.plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="pl-10 pr-8 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 appearance-none cursor-pointer"
          >
            <option value="all">All Plans</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">User</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Plan</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Joined</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{user.name}</p>
                        <p className="text-xs text-slate-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                      user.plan === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                      user.plan === 'pro' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {user.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      user.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        user.status === 'active' ? 'bg-green-400' : 'bg-red-400'
                      }`} />
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {new Date(user.created).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* User Count */}
      <p className="text-sm text-slate-400">
        Showing {filteredUsers.length} of {users.length} users
      </p>
    </div>
  );
}

// ============================================
// SUBSCRIPTIONS TAB
// ============================================
function SubscriptionsTab() {
  return (
    <div className="space-y-6">
      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-sm text-slate-400">Monthly Recurring Revenue</p>
          <p className="text-3xl font-bold text-green-400 mt-1">$1,247</p>
          <p className="text-xs text-green-400 mt-2">↑ 12% from last month</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-sm text-slate-400">Active Subscriptions</p>
          <p className="text-3xl font-bold text-white mt-1">8</p>
          <p className="text-xs text-slate-400 mt-2">5 Pro, 3 Enterprise</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-sm text-slate-400">Churn Rate</p>
          <p className="text-3xl font-bold text-white mt-1">2.1%</p>
          <p className="text-xs text-green-400 mt-2">↓ 0.5% from last month</p>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white font-medium">Recent Transactions</h3>
        </div>
        <div className="divide-y divide-slate-700/50">
          {[
            { user: 'Jane Smith', action: 'Upgraded to Enterprise', amount: '+$149', date: 'Today' },
            { user: 'Bob Wilson', action: 'Upgraded to Pro', amount: '+$49', date: 'Yesterday' },
            { user: 'Alice Brown', action: 'Renewed Pro', amount: '+$49', date: '3 days ago' },
            { user: 'Tom Davis', action: 'Cancelled Pro', amount: '-$49', date: '5 days ago' },
          ].map((tx, i) => (
            <div key={i} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-white">{tx.user}</p>
                <p className="text-xs text-slate-400">{tx.action}</p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${tx.amount.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount}
                </p>
                <p className="text-xs text-slate-400">{tx.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stripe Integration Notice */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium">Stripe Integration</p>
            <p className="text-sm text-slate-400 mt-1">
              Real-time subscription data will be available once Stripe webhooks are fully configured.
              Currently showing sample data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SETTINGS TAB
// ============================================
function SettingsTab() {
  return (
    <div className="space-y-6">
      {/* Platform Settings */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white font-medium">Platform Settings</h3>
        </div>
        <div className="p-4 space-y-4">
          <SettingRow 
            label="Maintenance Mode" 
            description="Disable access for all non-admin users"
            type="toggle"
          />
          <SettingRow 
            label="New User Registration" 
            description="Allow new users to create accounts"
            type="toggle"
            defaultChecked
          />
          <SettingRow 
            label="Free Trial Duration" 
            description="Number of days for free trial"
            type="input"
            defaultValue="14"
          />
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white font-medium">Email Configuration</h3>
        </div>
        <div className="p-4 space-y-4">
          <SettingRow 
            label="Email Provider" 
            description="Service used for sending emails"
            type="select"
            options={['Not Configured', 'SendGrid', 'Resend', 'Mailgun']}
          />
          <SettingRow 
            label="From Email" 
            description="Default sender email address"
            type="input"
            defaultValue="noreply@ltssanalytics.com"
          />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-500/10 rounded-xl border border-red-500/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-red-500/20">
          <h3 className="text-red-400 font-medium">Danger Zone</h3>
        </div>
        <div className="p-4 space-y-3">
          <button className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors">
            Clear All Cache
          </button>
          <button className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors ml-3">
            Reset Platform Stats
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================
function StatCard({ label, value, icon: Icon, color, loading }: { 
  label: string; 
  value: string | number; 
  icon: React.ElementType;
  color: string;
  loading?: boolean;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'text-blue-400 bg-blue-500/20',
    green: 'text-green-400 bg-green-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/20',
    purple: 'text-purple-400 bg-purple-500/20',
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-400">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className={`text-2xl font-bold ${loading ? 'text-slate-500' : 'text-white'}`}>
        {loading ? '...' : value}
      </p>
    </div>
  );
}

function ProgressBar({ label, value, total, color }: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses: Record<string, string> = {
    slate: 'bg-slate-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-400">{label}</span>
        <span className="text-white">{value} users</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick }: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}) {
  return (
    <button 
      onClick={onClick}
      className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl border border-slate-700 text-left transition-colors"
    >
      <Icon className="w-5 h-5 text-amber-400 mb-2" />
      <p className="text-sm text-white font-medium">{label}</p>
    </button>
  );
}

function SettingRow({ label, description, type, defaultValue, defaultChecked, options }: {
  label: string;
  description: string;
  type: 'toggle' | 'input' | 'select';
  defaultValue?: string;
  defaultChecked?: boolean;
  options?: string[];
}) {
  const [checked, setChecked] = useState(defaultChecked || false);

  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-white">{label}</p>
        <p className="text-xs text-slate-400">{description}</p>
      </div>
      {type === 'toggle' && (
        <button
          onClick={() => setChecked(!checked)}
          className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-slate-600'}`}
        >
          <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      )}
      {type === 'input' && (
        <input
          type="text"
          defaultValue={defaultValue}
          className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-white w-32 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      )}
      {type === 'select' && options && (
        <select className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      )}
    </div>
  );
}
